import React, {
  useState, useEffect, useRef, KeyboardEvent, useCallback, useMemo,
} from 'react';
import EditorJS, { LogLevels, API } from '@editorjs/editorjs';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';

import useStyles from './styles';
import { parseJsonToEditorState, onPasteEditorHandler, onKeyEditorHandler } from '../../services/editorJS/utils';
import { setArticleCommentFocusStatus } from '../../store/modules/articleComments/actions';
import hotKeyConverter from '../../utility/keyCodeConverter';
import { setAllowChangeProjectStatus } from '../../store/modules/projects/actions';

type Props = {
  name: string;
  id: number;
  value: string;
  onChangeValue: (str: string) => void;
  submit?: () => void;
  bottomPanel: React.ReactNode;
  disabled?: boolean;
};
// global variable for instanceEditor.onChange
let editorIsOnChange = true;

const ArticleCommentEditor = ({
  name,
  value,
  onChangeValue,
  bottomPanel,
  submit,
  disabled,
}:Props) => {
  const classes = useStyles();
  const data = useMemo(() => parseJsonToEditorState(value), [value]);
  const [instanceEditor, setInstanceEditor] = useState<EditorJS | undefined>();
  const refEditor = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [focus, setFocus] = useState(false);
  const [touch, setTouch] = useState(false);
  const focusHandler = useCallback((state: boolean) => {
    dispatch(setArticleCommentFocusStatus(state));
    setFocus(state);
  }, [dispatch]);

  useEffect(() => {
    const editor = refEditor.current;
    const onPaste = onPasteEditorHandler(instanceEditor, editor);

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
      setTouch(true);
      if (e.ctrlKey && e.keyCode === hotKeyConverter('enter')) {
        e.preventDefault();
        e.stopPropagation();
        if (submit) submit();
      }
    };
    if (editor !== null) {
      editor.addEventListener('paste', onPaste, true);
      // @ts-ignore
      editor.addEventListener('keydown', onKeyDownHandler, true);
    }

    return () => {
      if (editor !== null) {
        editor.removeEventListener('paste', onPaste, true);
        // @ts-ignore
        editor.removeEventListener('keydown', onKeyDownHandler, true);
      }
    };
  }, [refEditor, instanceEditor, submit, setTouch]);

  useEffect(() => {
    if (instanceEditor === undefined && refEditor.current !== null) {
      setInstanceEditor(new EditorJS({
        minHeight: 80,
        placeholder: 'Write a comment...',
        holder: refEditor.current,
        data,
        logLevel: 'ERROR' as LogLevels,
        onChange: (api: API) => {
          if (editorIsOnChange) {
            api.saver.save()
              .then((outputData) => {
                // setFieldValue(name as never, JSON.stringify(outputData));
                onChangeValue(JSON.stringify(outputData));
              });
          }
          editorIsOnChange = true;
        },
        onReady(): void {

        },
      }));
    }
  }, [instanceEditor, setInstanceEditor, data, refEditor, name, onChangeValue]);

  const testCallback = useCallback(() => {
    dispatch(setAllowChangeProjectStatus(false));
    onKeyEditorHandler(instanceEditor);
  }, [instanceEditor, dispatch]);

  return (
    <div>
      <div
        onFocus={() => focusHandler(true)}
        onBlur={() => focusHandler(false)}
        ref={refEditor}
        className={clsx(classes.editor, {
          [classes.editorWithBottom]: focus || touch,
        })}
        data-intercom-target="Script edit"
        data-placeholder="Custom placeholder"
        onKeyDown={() => testCallback()}
      />
      {bottomPanel && (
        <div
          className={clsx(classes.bottomPanel, {
            [classes.openedBottomPanel]: focus || touch,
          })}
        >
          {bottomPanel}
        </div>
      )}
    </div>
  );
};


export default ArticleCommentEditor;
